import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import store from '../../../store';
import { APP } from '../../../config';
import type { RootState } from '../../../store/models';
import { makeProfileMessageGenerator } from '../../../modules/referral/helpers';
import { WhoAmI } from '../../../store/CurrentUser.types';
import { ShareTarget } from '../../../modules/share';
import { getAndroidVersion, getIOSVersion } from '../../../modules/AppInfo';
import { callProvider } from '../../../modules/provider/callProvider';
import { ProviderReviewState } from '../../../store/ProviderReviews.model';
import { ProviderScheduleDay } from '../../../store/ProviderWorkSchedule.types';
import { RatingsSummary } from '../../../store/RatingsSummary.model';
import {
  Absolute,
  Stack,
  Touchable,
  PortalOverlay,
  HeaderText,
} from '../../ui';
import {
  useAssignTest,
  useAssignTests,
  useAsyncEffect,
  useGoBack,
  useInTest,
  useIsMobile,
  useLinkTo,
  useGoToChatWithPro,
  useUserStateRedux,
} from '../../../hooks';
import { DeprecatedProBio } from './ProBio';
import analytics from '../../../modules/analytics';
import useParams from '../../../hooks/useParams';
import { SearchPopoverModal } from '../../consumer/search/SearchPopover';
import { MARKETPLACE_BASE_NAME } from './MarketplaceProfile/constants';

import ProviderProfileTabs from './ProviderProfileTabs';
import { DeprecatedProfileImageGridContainer } from './ProfileImageGrid';
import useSearchImages from './ProviderProfileContainer/useSearchImages';
import DeprecatedSidebarInfo from './DeprecatedSidebarInfo';
import { urls as socialSharingUrls, messages as socialSharingMessages } from '../../consumer/sharing/social/helpers';
import { useProProfileShareFunctions } from './ProviderProfileContainer/useProProfileShareFunctions';
import { ShareOptions } from './types';
import { getShareOptionsForProShareProfile } from './ProviderProfileContainer/ProProfileShare';
import { ShareProps } from '../../consumer/sharing';
import { useEnterClientInIncentivesForOneAppointment } from '../../consumer/incentives/isInIncentivesTest';
import { RateThisAppModal, useRateThisAppOpen } from '../RateThisApp';
import { useShowReferralLandingIfNeeded } from '../../consumer/referrals/useShowReferralLandingIfNeeded';
import ProfileSentimentSurvey, { useProfileSentimentSurvey } from './ProfileSentimentSurvey';

import { useIncentivesPopovers } from '../../consumer/incentives/IncentivesPopover';
import { IncentivesBanner, useNCDAndDiscountInfo } from '../../consumer/incentives';
import { AboutIncentivesModalGotItCta } from '../../consumer/incentives/AboutIncentivesModal/AboutIncentivesModalGotItCta';
import HeaderServices from './HeaderServices';
import { ChevronIcon } from '../../ui/Icons';
import { REFERRAL_SHARE_KEY } from '../../../store/ShowIncentives.model';
import MarketplaceProfile from './MarketplaceProfile';
import { MarketplaceProfileModelState } from '../../../store/MarketplaceProfile/MarketplaceProfile.types';
import useProviderRecommendations from './hooks/useProviderRecommendations';
import LimitedAvailabilityBanner from './LimitedAvailabilityBanner';

import {
  COM_705_TEST_NAME,
  COM_705_TEST_PERCENT,
  COM_1253_TEST_NAME,
  COM_1253_TEST_PERCENT,
  HAIR_RELATED_SERVICE_CATEGORIES,
} from './HeaderServices/constants';

import { COM_806_TEST_NAME, COM_806_TEST_PERCENT } from '../../consumer/booking/ClientBookingSurvey/constants';

import { DeprecatedResponsiveProfileLayout } from './layout/ResponsiveProfileLayout';
import { getTopBookableServiceId } from './LimitedAvailabilityBanner/limitedAvailabilityBannerUtils';

import { StylieAwardProfileBanner } from '../../provider/stylies/StylieAwardProfileBanner';
import {
  CLIENT_INVITED_TO_BOOK,
  HOME_ROUTE,
  PROFILE_MESSAGE,
  PROFILE_ROUTE,
  PROVIDER_SETTINGS_ONLINE_BOOKING_MENU,
  PRO_PROMOTE_PROFILE,
} from '../../../route-names';
import { ClientProviderProfile } from '../../../api/ProfileClientView';
import { ProviderServiceGroup } from '../../../api/Providers/ServiceGroups';
import { IGalleryImage, ProfileGalleryImage } from '../../../api/Providers/GalleryImages';
import { SS_MESSAGING_SMS_OPTED_IN } from './constants';

import { flattenServiceGroups } from '../../../modules/provider/loadServiceGroups';
import { useMarketplaceProfile } from './MarketplaceProfile/hooks/useMarketplaceProfile';
import { AuthOrigins } from '../../../modules/user/constants';
import { NotifyMeRequestForChatModal } from '../Chat';
import { useIsStylieAwardee } from '../../../modules/stylies/utils';
import { TopProPopover } from '../TopPro/TopProPopover';
import { useTriggerLoginModal } from '../login/LoginModalProvider';
import { LoginModalMode } from '../login/LoginModalProvider/LoginModalProvider';

const SHOW_PROVIDER_SPECIALTIES_SWITCH_NAME = 'provider_specialties_fly-3112_20211213';
const PROVIDER_SPECIALTY_SWITCH_PERCENT = 0.5;

export type ProviderProfileProps = {
  profile: ClientProviderProfile;
  onBookmarkClick: () => void;
  reviews: ProviderReviewState;
  ratingsSummary: RatingsSummary;
  schedule: ProviderScheduleDay[];
  serviceGroups: ProviderServiceGroup[];
  galleryImages: IGalleryImage[];
  bookmarked: boolean;
  marketplaceProfile: MarketplaceProfileModelState;
  isOwnProfile: boolean;
};

export default function DeprecatedProviderProfile({
  profile,
  reviews,
  ratingsSummary,
  schedule,
  serviceGroups,
  galleryImages,
  bookmarked,
  onBookmarkClick,
  marketplaceProfile,
  isOwnProfile,
}: ProviderProfileProps) {
  const [showSpecality, setShowSpecialty] = useState(false);
  const [showTopProPopover, setShowTopProPopover] = useState<boolean>(false);
  const assignSpecialtyTest = useAssignTest(
    SHOW_PROVIDER_SPECIALTIES_SWITCH_NAME,
    PROVIDER_SPECIALTY_SWITCH_PERCENT,
  );
  const services = useMemo(() => flattenServiceGroups(serviceGroups), [serviceGroups]);

  useAsyncEffect(async () => {
    const inSpecialtyTest = await assignSpecialtyTest();
    setShowSpecialty(inSpecialtyTest.inTest);
  }, [assignSpecialtyTest]);

  useAssignTests([
    {
      name: COM_806_TEST_NAME,
      percent: COM_806_TEST_PERCENT,
    },
  ]);

  const routeParams = useParams();
  const goBack = useGoBack();
  const linkTo = useLinkTo();
  const isMobile = useIsMobile();
  const reviewCount = reviews?.total || 0;
  const ratingCount = ratingsSummary?.total || 0;
  const [
    marketplaceShowAllServicesClicked,
    setMarketplaceShowAllServicesClicked,
  ] = useState<boolean>(false);
  const [isServiceListExpanded, setIsServiceListExpanded] = useState<boolean>(false);
  const trackingId = useSelector<RootState, string>(
    state => state.trackingId.trackingId,
  );

  const showProfileLaunchSurvey = useProfileSentimentSurvey(profile);

  const {
    isIncentivesSearchOpen,
    setIncentivesSearchOpen,
    handleIncentivesSearchSubmitted,
    handleIncentivesSearchDismissed,
  } = useIncentivesPopovers();
  const ncdAndDiscountInfo = useNCDAndDiscountInfo(profile.id);
  const showMarketplace = marketplaceProfile.shouldRender;
  const maxServiceCount = useMemo(() => {
    if (showMarketplace && !marketplaceShowAllServicesClicked) {
      return marketplaceProfile.serviceListCount;
    }

    return null;
  }, [
    showMarketplace,
    marketplaceProfile.serviceListCount,
    marketplaceShowAllServicesClicked,
  ]);

  const calculatedMaxServiceCount = maxServiceCount;

  const currentUser = useSelector<RootState, RootState['user']>(
    state => state.user,
    (a, b) => a.userId === b.userId,
  );
  const isLoggedIn = !currentUser.is_anon;

  const canShowIncentivesBanner = profile?.name
    && !!ncdAndDiscountInfo
    && ncdAndDiscountInfo?.discountEligible
    && (ncdAndDiscountInfo?.isNewClientAppointment || !isLoggedIn);

  // COM-705
  const providerRecommendation = useProviderRecommendations(profile.id);
  const topCategory = providerRecommendation?.top_categories?.[0];

  const inCOM705Test = useInTest(COM_705_TEST_NAME);
  const assignCOM705Test = useAssignTest(
    COM_705_TEST_NAME,
    COM_705_TEST_PERCENT,
  );

  // COM-937
  const shouldShowChat = useSelector(
    store.select.chat.selectIsProChatEnabledById(profile.id),
  );

  // COM-1253
  const inProProfileFilterableTest = useInTest(COM_1253_TEST_NAME);
  const [
    showProProfileFilterableTest,
    setShowProProfileFilterableTest,
  ] = useState<boolean>(inProProfileFilterableTest);
  const assignProProfileFilterableTest = useAssignTest(
    COM_1253_TEST_NAME,
    COM_1253_TEST_PERCENT,
  );

  const logUserIn = useTriggerLoginModal();
  const goToChatWithPro = useGoToChatWithPro(profile);
  const [shouldRedirectToChat, setShouldRedirectToChat] = useState(false);
  const clientPhoneNumber = currentUser.phone_number;
  const [showUpdatePhoneNumberModal, setShowUpdatePhoneNumberModal] = useState<boolean>(false);
  const handleChatModalClose = () => {
    setShowUpdatePhoneNumberModal(false);
    setShouldRedirectToChat(false);
  };
  const [
    alreadyOptedIn, ,userStateLoaded,
  ] = useUserStateRedux(SS_MESSAGING_SMS_OPTED_IN);

  useEffect(() => {
    if (shouldRedirectToChat) {
      if (userStateLoaded && clientPhoneNumber && alreadyOptedIn) {
        goToChatWithPro();
      } else if (userStateLoaded && !alreadyOptedIn) {
        setShowUpdatePhoneNumberModal(true);
      }
    }
  }, [
    shouldRedirectToChat,
    goToChatWithPro,
    userStateLoaded,
    alreadyOptedIn,
    clientPhoneNumber,
  ]);

  const handleMessageClicked = async () => {
    if (isLoggedIn) {
      setShouldRedirectToChat(true);
    } else {
      const loggedInResponse = await logUserIn({
        mode: LoginModalMode.Login,
        origin: AuthOrigins.ProProfileMessageClick,
        loginRequired: true,
      });

      if (loggedInResponse?.userData?.phone_number) {
        setShouldRedirectToChat(true);
      } else {
        setShowUpdatePhoneNumberModal(true);
      }
    }
  };

  const hasServiceGroups = serviceGroups.length > 1;
  const showStickyServiceGroupTiles = inProProfileFilterableTest && isMobile && hasServiceGroups;
  useEffect(() => {
    // only assign test when landed pro has service groups;
    if (hasServiceGroups) {
      assignProProfileFilterableTest();
      setShowProProfileFilterableTest(showStickyServiceGroupTiles);
      if (showStickyServiceGroupTiles) {
        analytics.track('viewed', {
          component: 'StickyServiceGroupTiles',
        });
      }
    } else {
      setShowProProfileFilterableTest(false);
    }
  }, [
    inProProfileFilterableTest,
    showStickyServiceGroupTiles,
  ]);

  useEffect(() => {
    if (
      topCategory
      && HAIR_RELATED_SERVICE_CATEGORIES.includes(topCategory.category)
    ) {
      assignCOM705Test();
    }
  }, [
    assignCOM705Test,
    topCategory,
  ]);

  useMarketplaceProfile({ providerId: profile?.id });

  useEffect(() => {
    const {
      forwardToBook,
      source,
      time,
      date,
      serviceId,
      allServices,
    } = routeParams;
    /*
     * [ATL-4344] route to booking flow if all above params exist and are valid.
     * The "forwardToBook" comes from a SMS shortened link to the profile page.
     * We don't currently expose an endpoint that shortens links to the booking flow.
     */
    if (
      Boolean(forwardToBook)
      && !!source
      && !!time
      && !!date
      && (!!serviceId || !!allServices)
    ) {
      linkTo(CLIENT_INVITED_TO_BOOK, {
        providerId: profile.id,
        serviceId,
        allServices,
        time,
        date,
        source,
      });
    }
  }, [routeParams]);

  const canShowHeaderServiceTiles = !isOwnProfile;

  const socialShareUrls = socialSharingUrls.profile(profile);
  const socialShareMessages = socialSharingMessages.profile(
    profile,
    currentUser,
    socialShareUrls,
  );

  const proSocialShareFunctions = useProProfileShareFunctions(
    socialShareMessages,
    socialShareUrls,
    currentUser,
    profile?.id,
  );
  const proShareProfileSocialChannels: ShareOptions[] = useMemo(
    () => (socialShareUrls ? getShareOptionsForProShareProfile(socialShareUrls) : []),
    [socialShareUrls],
  );

  const handleProShareProfile = ({ type }: { type: ShareOptions }) => {
    if ([
      'instagram',
      'embed',
      'other',
    ].indexOf(type) >= 0) {
      analytics.track('pro_profile_promote_profile_clicked', {
        providerId: profile?.id,
      });
      linkTo(PRO_PROMOTE_PROFILE, { providerId: profile?.id });
    } else {
      proSocialShareFunctions[type]();
      analytics.track('client_proprofile_socialnetwork_click', {
        providerId: profile?.id,
        platform: type,
        channel: type,
        source: 'profile',
      });
    }
  };

  // Add to the incentives stack based on test name
  const enterClientIncentivesForReferral = useEnterClientInIncentivesForOneAppointment(
    REFERRAL_SHARE_KEY,
  );
  // On successful share action
  const handleReferralShare = () => {
    // Opt sharer into incentives
    enterClientIncentivesForReferral();
  };

  const generateProfileMessage = makeProfileMessageGenerator(profile);

  const shareConfig: Omit<ShareProps, 'user'> = {
    message: generateProfileMessage(currentUser as WhoAmI, trackingId),
    target: ShareTarget.PROFILE,
    provider: profile,
    onShareComplete: handleReferralShare,
  };

  useShowReferralLandingIfNeeded(
    {
      target: ShareTarget.PROFILE,
      providerId: profile.id,
    },
    {
      view_id: 'client_proprofile_client_referral_popover_viewed',
      click_id: 'client_proprofile_client_referral_popover_click',
      dismiss_id: 'client_proprofile_client_referral_popover_dismiss',
    },
    () => setIncentivesSearchOpen(true),
  );

  const [searchImages] = useSearchImages(profile.id, galleryImages);
  const showCallButton = !!(
    (getIOSVersion() || getAndroidVersion())
    && profile.public_phone_number?.length
  );

  const {
    is_awardee: isStylieAwardee,
    isLoading: isLoadingStyliesData,
  } = useIsStylieAwardee(profile);

  const onMessagePro = async () => {
    analytics.track('client_proprofile_message_click', {
      ...routeParams,
      profile_type: 'provider',
      provider_id: profile.id,
    });

    linkTo(PROFILE_MESSAGE, { providerId: profile.id });
  };

  const onCallPro = () => {
    analytics.track('client_proprofile_call_click', {
      ...routeParams,
      profile_type: 'provider',
      provider_id: profile.id,
    });

    callProvider(profile);
  };

  const onProfilePhotoClick = () => {
    analytics.track('client_proprofile_profile_pic_click', {
      provider_id: profile.id,
      image_url: profile.profile_photo,
    });
    linkTo(PROFILE_ROUTE, { providerId: profile.vanity_url });
  };

  const onProfileEditClick = () => {
    analytics.track('provider_update_info_icon_click');
    linkTo(PROVIDER_SETTINGS_ONLINE_BOOKING_MENU, {
      providerId: profile.id,
      source: 'profile',
    });
  };

  const handleAddressPress = () => {
    analytics.track('client_proprofile_address_click');
  };

  const handleShowAllServices = useCallback(() => {
    analytics.track(`${MARKETPLACE_BASE_NAME}_see_all_services_clicked`);
    setMarketplaceShowAllServicesClicked(true);
    setIsServiceListExpanded(true);
  }, []);

  const { handleClose: handleCloseRateThisApp, isRateThisAppOpen } = useRateThisAppOpen(null);

  const handleLimitedAvailabilityBannerPressed = useCallback(() => {
    analytics.track('pro_profile_limited_availability_pressed', {
      provider_id: profile.id,
    });
  }, [profile.id]);

  const handleTopProClick = useCallback(() => {
    analytics.track('click', {
      component: 'top_pro_badge',
      provider_id: profile.id,
    });
    setShowTopProPopover(true);
  }, [profile.id]);

  const handleTopProPopoverClose = () => {
    setShowTopProPopover(false);
  };

  const highlightedServiceId = routeParams?.matchedServiceId
    ? parseInt(routeParams.matchedServiceId, 10)
    : null;

  const topBookableServiceId = useMemo(() => getTopBookableServiceId(services), [services]);

  /*
    Profile bottom  ( if pro service list
    is less than 6 in desktop or 4 in mobile,
    do not show the MP)
  */
  const showBottomSlot = !isMobile ? services?.length > 5 : services?.length > 3;

  if (isLoadingStyliesData) {
    return null;
  }

  return (
    <React.Fragment>
      <DeprecatedResponsiveProfileLayout
        showProProfileFilterableTest={showProProfileFilterableTest}
        bannerSlot={(
          <React.Fragment>
            {(canShowHeaderServiceTiles && !showProProfileFilterableTest) && (
              <HeaderServices
                inCOM705Test={inCOM705Test}
                providerId={profile.id}
              />
            )}

            {canShowIncentivesBanner && (
              <Box>
                <IncentivesBanner
                  providerName={profile?.name}
                  discountDetails={ncdAndDiscountInfo}
                  renderMoreDetailsCta={onDismiss => (
                    <AboutIncentivesModalGotItCta onDismiss={onDismiss} />
                  )}
                />
              </Box>
            )}
            {/* Back button for app-only guest users */}
            {!!APP && !isLoggedIn ? (
              <Absolute top={72} left={2} zIndex={2}>
                <Touchable onClick={() => goBack(HOME_ROUTE)}>
                  <Box color={BrandColor.WhiteWash} padding={8}>
                    <ChevronIcon
                      direction="left"
                      color={BrandColor.BackInBlack}
                      height={20}
                      width={20}
                    />
                  </Box>
                </Touchable>
              </Absolute>
            ) : null}
          </React.Fragment>

        )}
        leftColumnSlot={(
          <Stack space={8}>
            {/* Mobile gallery preview */}
            {isMobile ? (
              <Box>
                <DeprecatedProfileImageGridContainer
                  galleryImages={
                    searchImages as ProfileGalleryImage[]
                  }
                  profile={profile}
                  isEditMode={false}
                  isOwnProfile={false}
                  clickParams={{
                    ...routeParams,
                    search_id: routeParams.sid,
                  }}
                  disableBottomPadding={isStylieAwardee}
                />
                {isStylieAwardee ? (
                  <Box paddingBottom={12}>
                    <StylieAwardProfileBanner profile={profile} />
                  </Box>
                ) : null}
              </Box>
            ) : null}

            {/* Pro Bio */}
            <Box paddingX={isMobile ? 16 : 0}>
              <DeprecatedProBio
                isEditMode={false}
                isTopPro={profile.is_top_pro}
                isOwnProfile={false}
                provider={profile}
                ratingCount={ratingCount}
                reviewCount={reviewCount}
                isBookmarked={bookmarked}
                onMessagePro={onMessagePro}
                onCallPro={onCallPro}
                showCallPro={showCallButton}
                onProfilePhotoClick={onProfilePhotoClick}
                onBookmarkClick={onBookmarkClick}
                onProfileEditClick={onProfileEditClick}
                shareConfig={shareConfig}
                shouldShowChat={shouldShowChat}
                onMessageClicked={handleMessageClicked}
                onTopProClick={handleTopProClick}
              />
            </Box>
            <LimitedAvailabilityBanner
              onPress={handleLimitedAvailabilityBannerPressed}
              providerBookable={profile.is_effectively_online_bookable}
              providerId={profile.id}
              providerName={profile.name}
              topBookableServiceId={topBookableServiceId}
            />
            <ProviderProfileTabs
              serviceGroups={serviceGroups}
              showProProfileFilterableTest={showProProfileFilterableTest}
              hasServices={services?.length > 0}
              highlightedServiceId={highlightedServiceId}
              onCallPro={onCallPro}
              profile={profile}
              onMessagePro={shouldShowChat ? handleMessageClicked : onMessagePro}
              showCallButton={showCallButton}
              maxServiceCount={calculatedMaxServiceCount}
              onShowAllServices={handleShowAllServices}
              shouldShowSpecialties={showSpecality}
              hasProviderRecommendationData={providerRecommendation?.found}
              isServiceListExpanded={isServiceListExpanded}
            />
          </Stack>
        )}
        rightColumnSlot={(
          <DeprecatedSidebarInfo
            profile={profile}
            isEditMode={false}
            isOwnProfile={false}
            onAddressPress={handleAddressPress}
            showCallButton={showCallButton}
            onCallPro={onCallPro}
            onMessagePro={onMessagePro}
            shareOptions={proShareProfileSocialChannels}
            onShare={handleProShareProfile}
            schedule={schedule}
            galleryImages={searchImages}
          />
        )}
        bottomSlot={showBottomSlot && (
          <MarketplaceProfile
            marketplaceProfile={marketplaceProfile}
            providerRecommendationData={providerRecommendation}
            providerLatitude={profile?.coords?.latitude}
            providerLongitude={profile?.coords?.longitude}
            providerCity={profile?.city}
          />
        )}
      />
      {/* MODALS */}
      {isIncentivesSearchOpen && (
        <SearchPopoverModal
          showIncentivesStar
          source="incentives_profile_search"
          onSubmit={handleIncentivesSearchSubmitted}
          onClose={handleIncentivesSearchDismissed}
        />
      )}
      {isRateThisAppOpen && (
        <RateThisAppModal onClose={handleCloseRateThisApp} />
      )}
      {showProfileLaunchSurvey ? (
        <ProfileSentimentSurvey profile={profile} />
      ) : null}
      {showUpdatePhoneNumberModal && (
        <PortalOverlay name="booking-notify-me-request-modal">
          <NotifyMeRequestForChatModal
            user={currentUser}
            headerText={(
              <HeaderText level={1} testID="client_notify_me_for_chat_modal_bell_header">
                Get notified when
                <HeaderText level={1} color={BrandColor.DarkBlue}>
                  {` ${profile.name} `}
                </HeaderText>
                responds
              </HeaderText>
            )}
            trackingParams={{
              type: 'date-selection',
              provider_id: profile.id,
            }}
            onClose={handleChatModalClose}
            onSuccess={goToChatWithPro}
          />
        </PortalOverlay>
      )}
      {showTopProPopover && (
        <TopProPopover
          providerName={profile.name}
          onBackdropClick={handleTopProPopoverClose}
          onClose={handleTopProPopoverClose}
        />
      )}
    </React.Fragment>
  );
}
